import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, IconButton } from '@material-ui/core';
import {
  CheckRounded,
  CloseRounded,
  ErrorOutlineRounded,
  WarningRounded,
} from '@material-ui/icons';

import styles from './BundleOperationDialog.module.scss';

function BundleGlyph() {
  return (
    <svg
      className={styles.bundleGlyph}
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M16 3.75 27 9.5 16 15.25 5 9.5 16 3.75Z" />
      <path d="m5 15.25 11 5.75 11-5.75" />
      <path d="m5 21 11 5.75L27 21" />
    </svg>
  );
}

function StatusVisual({ state }) {
  if (state === 'success') {
    return <CheckRounded className={styles.stateIcon} aria-hidden="true" />;
  }
  if (state === 'warning') {
    return <WarningRounded className={styles.stateIcon} aria-hidden="true" />;
  }
  if (state === 'error') {
    return <ErrorOutlineRounded className={styles.stateIcon} aria-hidden="true" />;
  }

  return (
    <span className={styles.buildVisualInner}>
      <span className={styles.progressOrbit} aria-hidden="true" />
    </span>
  );
}

StatusVisual.propTypes = {
  state: PropTypes.oneOf(['building', 'success', 'warning', 'error']).isRequired,
};

export default function BundleOperationDialog({
  open,
  state,
  title,
  heading,
  description,
  detail,
  detailKind,
  actions,
  onClose,
  closeLabel,
  closeTestId,
  progressLabel,
  progressTestId,
  testId,
}) {
  const pending = state === 'building';
  const titleId = `${testId}-title`;
  const descriptionId = `${testId}-description`;
  const liveRole = state === 'error' ? 'alert' : 'status';

  const handleClose = (event, reason) => {
    if (pending) return;
    if (onClose) onClose(event, reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={pending}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      BackdropProps={{ className: styles.backdrop }}
      PaperProps={{
        className: styles.paper,
        'data-testid': testId,
      }}
    >
      <header className={styles.topbar}>
        <div className={styles.identity}>
          <span className={styles.identityMark} aria-hidden="true">
            <BundleGlyph />
          </span>
          <h2 id={titleId}>{title}</h2>
        </div>
        {!pending && (
          <IconButton
            className={styles.closeButton}
            aria-label={closeLabel}
            onClick={onClose}
            data-testid={closeTestId}
          >
            <CloseRounded />
          </IconButton>
        )}
      </header>

      <div
        className={styles.content}
        role={liveRole}
        aria-live={state === 'error' ? 'assertive' : 'polite'}
        aria-atomic="true"
      >
        <div
          className={`${styles.statusVisual} ${styles[`statusVisual--${state}`]}`}
          {...(pending ? {
            role: 'progressbar',
            'aria-label': progressLabel || heading,
            'data-testid': progressTestId || `${testId}-progress`,
          } : {})}
        >
          <StatusVisual state={state} />
        </div>

        <div className={styles.statusCopy}>
          <h3>{heading}</h3>
          <p id={descriptionId}>{description}</p>
        </div>

        {detail && (
          <div
            className={`${styles.detail} ${styles[`detail--${detailKind || state}`]}`}
            data-testid={`${testId}-detail`}
          >
            {detail}
          </div>
        )}
      </div>

      {actions && <footer className={styles.actions}>{actions}</footer>}
    </Dialog>
  );
}

BundleOperationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  state: PropTypes.oneOf(['building', 'success', 'warning', 'error']).isRequired,
  title: PropTypes.node.isRequired,
  heading: PropTypes.node.isRequired,
  description: PropTypes.node.isRequired,
  detail: PropTypes.node,
  detailKind: PropTypes.oneOf(['success', 'warning', 'error', 'info']),
  actions: PropTypes.node,
  onClose: PropTypes.func,
  closeLabel: PropTypes.string,
  closeTestId: PropTypes.string,
  progressLabel: PropTypes.string,
  progressTestId: PropTypes.string,
  testId: PropTypes.string,
};

BundleOperationDialog.defaultProps = {
  detail: null,
  detailKind: null,
  actions: null,
  onClose: null,
  closeLabel: 'Close',
  closeTestId: null,
  progressLabel: null,
  progressTestId: null,
  testId: 'bundle-operation-dialog',
};
